import React from "react";
import "@/App.css";
import { BrowserRouter, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { AskSection } from "@/components/AskSection";
import { WisdomWall } from "@/components/WisdomWall";
import { ContentStudio } from "@/components/ContentStudio";
import { About, Footer } from "@/components/About";
import { AuthDialog } from "@/components/AuthDialog";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { AuthCallback } from "@/components/AuthCallback";
import { BetaBanner } from "@/components/BetaBanner";
import { FreeBanner } from "@/components/FreeBanner";
import { SessionsSection } from "@/components/SessionsSection";
import { RatingSection, RatingDialog, hasRated } from "@/components/Rating";

const MainApp = () => {
  const [presetProblem, setPresetProblem] = React.useState("");
  const [ratingOpen, setRatingOpen] = React.useState(false);
  const wallRef = React.useRef(null);
  const askRef = React.useRef(null);
  const { user } = useAuth();

  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const payment = params.get("payment");
    const session = params.get("session");
    if (payment) {
      if (payment === "success") toast.success("Payment successful — welcome to Shabba Robo Premium! 🎉");
      else if (payment === "failed") toast.error("Payment was not completed. Please try again.");
      else if (payment === "invalid") toast.error("We couldn't find that payment.");
      window.history.replaceState(null, "", "/");
    }
    if (session) {
      if (session === "success") toast.success("Session booked & paid! 🎉 You'll receive your meeting link shortly.");
      else if (session === "failed") toast.error("Session payment was not completed. Please try again.");
      else if (session === "invalid") toast.error("We couldn't find that booking.");
      window.history.replaceState(null, "", "/");
    }
  }, []);

  const handleQuickAsk = (text) => {
    setPresetProblem(text + " \u200b");
    setTimeout(() => document.getElementById("ask")?.scrollIntoView({ behavior: "smooth" }), 50);
  };

  const handleAnswered = () => {
    wallRef.current?.refresh?.();
    askRef.current?.refreshHistory?.();
    if (!hasRated()) {
      setTimeout(() => setRatingOpen(true), 1200);
    }
  };

  return (
    <div className="App">
      <FreeBanner />
      <BetaBanner />
      <Navbar />
      <main>
        <Hero onQuickAsk={handleQuickAsk} />
        <AskSection ref={askRef} presetProblem={presetProblem} onAnswered={handleAnswered} />
        <WisdomWall ref={wallRef} />
        <ContentStudio />
        <SessionsSection />
        <RatingSection />
        <About />
      </main>
      <Footer />
      <AuthDialog />
      <UpgradeDialog />
      <RatingDialog open={ratingOpen} onOpenChange={setRatingOpen} />
      <Toaster position="top-center" richColors />
    </div>
  );
};

const Root = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return <MainApp />;
};

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Root />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
